import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ADPResourceService } from 'digital-core';
import { filter, take } from 'rxjs/operators';
import { PGHAppLoadService } from './pgh-app-load.service';
import { parseSiteRedirectMapping, resolveSiteRedirectUrl, SITE_REDIRECT_MAPPING_KEY } from '../utils/site-redirect.util';

/**
 * Domain-migration redirect driven by the CMS portal setting `Global_OldToNewSite_Redirect_Mapping`
 * (`olddomain|newdomain`). Once site settings load, if the value holds a `|` pair and the browser is on
 * the old domain, the browser is sent to the same path/query/fragment on the new domain. Anything
 * else — unset, blank, no `|`, a different host — is a no-op, so it is safe to ship to every client.
 */
@Injectable({ providedIn: 'root' })
export class SiteRedirectService {
  private appLoadService = inject(PGHAppLoadService);
  private resourceService = inject(ADPResourceService);
  private document = inject(DOCUMENT);

  private started = false;

  /** Idempotent — call once at app start; waits for the Global site settings to arrive. */
  init(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.appLoadService.isSiteResourcesLoaded
      .pipe(
        filter((loaded) => !!loaded),
        take(1)
      )
      .subscribe({
        next: () => this.redirectIfMapped(),
        error: () => undefined,
      });
  }

  private redirectIfMapped(): void {
    const mapping = parseSiteRedirectMapping(this.resourceService.getPortalSetting(SITE_REDIRECT_MAPPING_KEY, true)?.Value);
    const location = this.document.defaultView?.location;
    if (!mapping || !location) {
      return;
    }
    const target = resolveSiteRedirectUrl(mapping, location);
    if (target) {
      this.navigate(target);
    }
  }

  /** Seam for tests — `window.location.replace` can't be spied on directly. */
  protected navigate(url: string): void {
    this.document.defaultView?.location.replace(url);
  }
}
